import { useState } from "react";
import { Menu, X } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  UserProfileImage,
} from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { MessageSquareText, Sun, Moon } from "lucide-react";
import { useThemeStore } from "@/store/themeStore";
import { cn } from "@/libs/utils";
import { useChatStore } from "@/store/chatStore";
import { useAuthStore } from "@/store/authStore";

type SidebarItem = {
  icon: React.ElementType;
  label: string;
  onClick?: () => void;
  active?: boolean;
};

function SidebarButton({ item }: { item: SidebarItem }) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className={cn(
        "flex items-center justify-center w-12 h-12 rounded-md hover:bg-white/10 transition-colors",
        item.active ? "text-white" : "text-white/70"
      )}
      aria-label={item.label}
      title={item.label}
      onClick={item.onClick}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
}

export function MobileSidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const { theme, toggleTheme } = useThemeStore();
  const { user } = useAuthStore();

  const items: SidebarItem[] = [
    { icon: MessageSquareText, label: "Chats", active: true },
  ];

  return (
    <>
      {/* Hamburger Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(true)}
        className="md:hidden"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Mobile Sidebar Overlay (animated) */}
      <div
        className={cn(
          "fixed inset-0 z-50 md:hidden transition-opacity duration-300",
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        )}
        aria-hidden={!isOpen}
      >
        {/* Backdrop */}
        <div
          className={cn(
            "fixed inset-0 bg-black/50 transition-opacity duration-300",
            isOpen ? "opacity-100" : "opacity-0"
          )}
          onClick={() => setIsOpen(false)}
        />

        {/* Sidebar */}
        <div
          className={cn(
            "fixed left-0 top-0 h-full w-16 bg-primary border-r shadow-lg transform transition-transform duration-300 ease-in-out",
            isOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="flex flex-col h-full">
            {/* Close Button */}
            <div className="flex items-center justify-center p-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="w-12 h-12"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Menu Items */}
            <div className="flex-1 flex flex-col items-center gap-2 py-2">
              {items.map((item) => (
                <SidebarButton
                  key={item.label}
                  item={{ ...item, active: true }}
                />
              ))}
            </div>

            {/* Bottom Section */}
            <div className="p-2 flex items-center justify-center">
              <SidebarButton
                item={{
                  icon: theme === "light" ? Moon : Sun,
                  label: theme === "light" ? "Dark Mode" : "Light Mode",
                  onClick: toggleTheme,
                  active: true,
                }}
              />
            </div>
            <div className="p-2 pb-3 flex items-center justify-center">
              <UserProfileImage userId={user?.id} />

              {/* <Avatar className="h-10 w-10">
                <AvatarImage src={user?.user_image} alt={user?.username} />
                <AvatarFallback>
                  {user?.username ? user.username : "YO"}
                </AvatarFallback>
              </Avatar> */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MobileSidebar;
